/**
 * Client for `/api/baselines`.
 *
 * A baseline is a named, curated set of BIN 10 events plus the curves fitted over them, one set per
 * boat CONFIG. The list endpoint deliberately omits `fit_json` (it is large); call `fetchBaseline`
 * when you actually need the curves to draw.
 */

import { apiEndpoints } from '../config/env';
import { getData, postData, putData, deleteData } from '../utils/global';
import { error as logError } from '../utils/console';

/** One fitted channel at one aggregate type, sampled onto the shared TWS grid. */
export interface BaselineFitChannel {
  /** Aligned index-for-index with `BaselineFitJson.tws_grid`; null outside the members' own range. */
  values: Array<number | null>;
  method: 'loess' | 'linear' | 'mean';
  /** Member samples that contributed. */
  n: number;
}

/** Channel name -> aggregate type -> curve. */
export type BaselineFitChannels = Record<string, Record<string, BaselineFitChannel>>;

export interface BaselineFitLeg {
  /** Member events on this leg for this config. */
  n: number;
  channels: BaselineFitChannels;
}

export type BaselineLegKey = 'UPWIND' | 'DOWNWIND';

export interface BaselineFitJson {
  version: number;
  bandwidth: number;
  tws_channel: string;
  /** Shared ascending TWS grid, kph. */
  tws_grid: number[];
  /** The (channel, agrType) pairs this fit was built from. */
  channels?: Array<{ channel: string; agrType: string }>;
  configs: Record<string, Partial<Record<BaselineLegKey, BaselineFitLeg>>>;
  /**
   * Maneuver curves saved onto this same baseline. Absent until maneuvers have been saved.
   * A baseline that is maneuvers only does not use this field — that fit is the document itself.
   */
  maneuver?: ManeuverBaselineFitJson;
}

/** A baseline is either a straight-line reference or a maneuver reference. */
export type BaselineKind = 'straight_line' | 'maneuver';

export type ManeuverEventTypeKey = 'TACK' | 'GYBE' | 'ROUNDUP' | 'BEARAWAY' | 'TAKEOFF';

/** One fitted maneuver channel, sampled onto the shared TWS grid. No aggregate-type dimension. */
export interface ManeuverBaselineFitChannel {
  /** Aligned index-for-index with `tws_grid`; null outside the members' own TWS range. */
  values: Array<number | null>;
  method: 'loess' | 'linear' | 'mean';
  n: number;
}

/** A member maneuver of one config+type bucket, kept for the map/timeseries overlays. */
export interface ManeuverBaselineMember {
  event_id: number;
  /** Tws_avg, kph. */
  tws: number;
  /** Entry tack: 1 starboard, -1 port, null when the entry angle is missing. */
  side: 1 | -1 | null;
}

export interface ManeuverBaselineFitBucket {
  n: number;
  members: ManeuverBaselineMember[];
  /** Lowercase channel key (matching the maneuver history rows) -> curve. */
  channels: Record<string, ManeuverBaselineFitChannel>;
}

/** fit_json for kind='maneuver': configs -> event type -> fitted channels over TWS. */
export interface ManeuverBaselineFitJson {
  version: number;
  kind: 'maneuver';
  bandwidth: number;
  tws_channel: string;
  /** Shared ascending TWS grid, kph. */
  tws_grid: number[];
  configs: Record<string, Partial<Record<ManeuverEventTypeKey, ManeuverBaselineFitBucket>>>;
}

/**
 * True when the document itself is a maneuver fit.
 *
 * A baseline that also holds straight-line curves keeps those at the top and nests the maneuver
 * fit under `maneuver`, so this stays false and the Performance charts keep reading `configs`.
 */
export function isManeuverBaselineFit(
  fit: BaselineFitJson | ManeuverBaselineFitJson | null | undefined
): fit is ManeuverBaselineFitJson {
  return !!fit && (fit as ManeuverBaselineFitJson).kind === 'maneuver';
}

/**
 * The maneuver curves on a baseline, whether they are the whole document or nested beside a
 * straight-line fit. Null when this baseline has no maneuvers saved.
 */
export function readManeuverBaselineFit(
  fit: BaselineFitJson | ManeuverBaselineFitJson | null | undefined
): ManeuverBaselineFitJson | null {
  if (!fit) { return null; }
  const nested = (fit as BaselineFitJson).maneuver;
  if (nested && nested.kind === 'maneuver') { return nested; }
  if (isManeuverBaselineFit(fit)) { return fit; }
  return null;
}

export interface BaselineRow {
  baseline_id: number;
  project_id: number;
  name: string;
  description?: string | null;
  kind?: string;
  created_by?: string | null;
  created_at?: string;
  updated_at?: string;
  event_count?: number;
  /** Present only on the single-baseline fetch. Shape depends on `kind`. */
  fit_json?: BaselineFitJson | ManeuverBaselineFitJson | null;
}

/** A channel plus the aggregate type it should be fitted at. */
export interface BaselineChannelPair {
  channel: string;
  agr_type: string;
}

export interface BaselineMutationResult {
  success: boolean;
  message?: string;
  data?: any;
}

function baseQuery(className: string, projectId: number | string): string {
  return `class_name=${encodeURIComponent(className)}&project_id=${encodeURIComponent(projectId)}`;
}

/**
 * All baselines for a project, most recently updated first. Never includes `fit_json`.
 * Pass `kind` so a display picker lists baselines that have that side. The save dialog omits it:
 * one name is shared by performance and maneuvers.
 */
export async function listBaselines(
  className: string,
  projectId: number | string,
  options?: { kind?: BaselineKind; signal?: AbortSignal }
): Promise<BaselineRow[]> {
  const kindQuery = options?.kind ? `&kind=${encodeURIComponent(options.kind)}` : '';
  const url = `${apiEndpoints.app.baselines}?${baseQuery(className, projectId)}${kindQuery}`;
  const result = await getData(url, options?.signal);
  if (!result.success || !Array.isArray(result.data)) { return []; }
  return result.data as BaselineRow[];
}

/** One baseline including its fitted curves, or null when it no longer exists. */
export async function fetchBaseline(
  className: string,
  projectId: number | string,
  baselineId: number,
  signal?: AbortSignal
): Promise<BaselineRow | null> {
  const url = `${apiEndpoints.app.baselines}/${encodeURIComponent(baselineId)}?${baseQuery(className, projectId)}`;
  const result = await getData(url, signal);
  if (!result.success || !result.data) { return null; }
  return result.data as BaselineRow;
}

export interface SaveBaselineArgs {
  className: string;
  projectId: number | string;
  eventIds: number[];
  /** Which (channel, aggregate) curves to fit — derived from the page's charts. */
  channels: BaselineChannelPair[];
  source?: string;
  signal?: AbortSignal;
}

/** Create a new baseline from the current selection and fit it. */
export async function createBaseline(
  args: SaveBaselineArgs & { name: string; description?: string; kind?: BaselineKind }
): Promise<BaselineMutationResult> {
  try {
    const result = await postData(
      apiEndpoints.app.baselines,
      {
        class_name: args.className,
        project_id: args.projectId,
        name: args.name,
        description: args.description,
        kind: args.kind || 'straight_line',
        event_ids: args.eventIds,
        channels: args.channels,
        source: args.source || 'lasso',
      },
      args.signal
    );
    return { success: !!result.success, message: result.message || result.error, data: result.data };
  } catch (err: unknown) {
    logError('[baselinesService] createBaseline failed', err);
    return { success: false, message: err instanceof Error ? err.message : 'Create failed' };
  }
}

/** Add the current selection to an existing baseline; the server refits over the union. */
export async function appendBaselineEvents(
  args: SaveBaselineArgs & { baselineId: number }
): Promise<BaselineMutationResult> {
  try {
    const result = await postData(
      `${apiEndpoints.app.baselines}/${encodeURIComponent(args.baselineId)}/events`,
      {
        class_name: args.className,
        project_id: args.projectId,
        event_ids: args.eventIds,
        channels: args.channels,
        source: args.source || 'lasso',
      },
      args.signal
    );
    return { success: !!result.success, message: result.message || result.error, data: result.data };
  } catch (err: unknown) {
    logError('[baselinesService] appendBaselineEvents failed', err);
    return { success: false, message: err instanceof Error ? err.message : 'Save failed' };
  }
}

/** Rename or re-describe a baseline. */
export async function updateBaseline(
  className: string,
  projectId: number | string,
  baselineId: number,
  fields: { name?: string; description?: string }
): Promise<BaselineMutationResult> {
  try {
    const result = await putData(
      `${apiEndpoints.app.baselines}/${encodeURIComponent(baselineId)}`,
      { class_name: className, project_id: projectId, ...fields }
    );
    return { success: !!result.success, message: result.message || result.error, data: result.data };
  } catch (err: unknown) {
    logError('[baselinesService] updateBaseline failed', err);
    return { success: false, message: err instanceof Error ? err.message : 'Update failed' };
  }
}

/**
 * Store an exported fit as a new baseline. Membership stays empty — the curves are the snapshot.
 * `nameConflict: "rename"` appends " (import)" when the name is already used.
 */
export async function importBaselineSnapshot(args: {
  className: string;
  projectId: number | string;
  name: string;
  description?: string | null;
  fitJson: unknown;
  nameConflict: 'rename' | 'overwrite';
}): Promise<BaselineMutationResult> {
  try {
    const result = await postData(
      `${apiEndpoints.app.baselines}/import`,
      {
        class_name: args.className,
        project_id: args.projectId,
        name: args.name,
        description: args.description,
        fit_json: args.fitJson,
        name_conflict: args.nameConflict,
      }
    );
    return { success: !!result.success, message: result.message || result.error, data: result.data };
  } catch (err: unknown) {
    logError('[baselinesService] importBaselineSnapshot failed', err);
    return { success: false, message: err instanceof Error ? err.message : 'Import failed' };
  }
}

/**
 * Clear coverage cells on one configuration.
 *
 * Events whose wind speed lands in those cells are removed and the curves are refit. The columns
 * themselves are nulled so a neighbouring wind speed does not keep the cell filled. Saving events
 * onto the baseline later fits it again from what remains.
 */
export async function clearBaselineCells(args: {
  className: string;
  projectId: number | string;
  baselineId: number;
  config: string;
  cells: Array<{ slice: string; tws: number }>;
}): Promise<BaselineMutationResult> {
  try {
    const result = await postData(
      `${apiEndpoints.app.baselines}/${encodeURIComponent(args.baselineId)}/cells`,
      {
        class_name: args.className,
        project_id: args.projectId,
        config: args.config,
        cells: args.cells,
      }
    );
    return { success: !!result.success, message: result.message || result.error, data: result.data };
  } catch (err: unknown) {
    logError('[baselinesService] clearBaselineCells failed', err);
    return { success: false, message: err instanceof Error ? err.message : 'Clear failed' };
  }
}

/** Delete a baseline and its membership. */
export async function deleteBaseline(
  className: string,
  projectId: number | string,
  baselineId: number
): Promise<BaselineMutationResult> {
  try {
    const url = `${apiEndpoints.app.baselines}/${encodeURIComponent(baselineId)}?${baseQuery(className, projectId)}`;
    const result = await deleteData(url, {});
    return { success: !!result.success, message: result.message || result.error, data: result.data };
  } catch (err: unknown) {
    logError('[baselinesService] deleteBaseline failed', err);
    return { success: false, message: err instanceof Error ? err.message : 'Delete failed' };
  }
}

/** Config keys a baseline holds a fit for, sorted. Both fit shapes key configs the same way. */
export function baselineConfigs(
  fit: BaselineFitJson | ManeuverBaselineFitJson | null | undefined
): string[] {
  if (!fit?.configs) { return []; }
  return Object.keys(fit.configs).sort();
}
